import clsx from 'clsx';
import { useMemo, useState, useEffect } from 'react';

import {
  IconUsers,
  IconSchool,
  IconMoodKid,
  IconDeviceGamepad2,
} from '@tabler/icons-react';

import { AvatarGroupList } from '@/components/Avatar';
import Select from '@/components/Select';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table/index';
import { SubscriptionStatusConfig } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';
import {
  getOverviewStats,
  getTopSchoolsGraph,
  getLearningStageGraph,
  getUserEngagementGraph,
  getSubscriptionDistribution,
  getTopSchoolsLicenseAnalytics,
} from '@/service/reportService';
import useCommonStore from '@/store/useCommonStore';
import {
  IStatsDataItem,
  TTopSchoolsGraphItem,
  ITopSchoolLicenseItem,
  TLearningStageGraphItem,
  IReportOverviewStatsData,
  TUserEngagementGraphItem,
  TSubscriptionDistributionItem,
} from '@/types';
import { formatUnixToLongDate } from '@/utils';

import LearningLevelDistributionChart from './components/LearningLevelDistributionChart';
import SchoolActiveStudentsChart from './components/SchoolActiveStudentsChart';
import SubscriptionDistributionChart from './components/SubscriptionDistributionChart';
import UserEngagementOverTimeChart from './components/UserEngagementOverTimeChart';

// ─── Summary Config ───────────────────────────────────────────────────────────

const OVERVIEW_SUMMARY_CONFIG: IStatsDataItem[] = [
  {
    key: 'totalActiveChildren',
    icon: <IconMoodKid color="#717784" />,
    label: 'report-analytics.overview.summary-cards.total-active-children',
    value: '0',
    trendLabel: 'common.this-month',
    trendDirection: 'UP',
    isPositive: true,
  },
  {
    key: 'activeParents',
    icon: <IconUsers color="#717784" />,
    label: 'report-analytics.overview.summary-cards.active-parents',
    value: '0',
    trendLabel: 'common.this-month',
    trendDirection: 'UP',
    isPositive: true,
  },
  {
    key: 'activeSchools',
    icon: <IconSchool color="#717784" />,
    label: 'report-analytics.overview.summary-cards.active-schools',
    value: '0',
    trendLabel: 'common.this-month',
    trendDirection: 'UP',
    isPositive: true,
  },
  {
    key: 'totalGamesPlayed',
    icon: <IconDeviceGamepad2 color="#717784" />,
    label: 'report-analytics.overview.summary-cards.total-games-played',
    value: '0',
    trendLabel: 'common.this-month',
    trendDirection: 'UP',
    isPositive: true,
  },
];

const USER_ENGAGEMENT_PERIOD_OPTIONS = [
  { value: 'yearly', label: 'dashboard.filters.yearly' },
  { value: 'monthly', label: 'dashboard.filters.monthly' },
  { value: 'weekly', label: 'dashboard.filters.weekly' },
];

const USER_ENGAGEMENT_PERIOD_FILTER_MAP: Record<string, number> = {
  yearly: 1,
  monthly: 2,
  weekly: 3,
};

const SUBSCRIPTION_DISTRIBUTION_PALETTE = [
  '#9333EA',
  '#DB2777',
  '#F59E0B',
  '#2563EB',
  '#10B981',
];

// ─── Component ────────────────────────────────────────────────────────────────

const OverView = () => {
  const { formatText } = useFormatText();
  const [statsData, setStatsData] = useState<IStatsDataItem[]>([]);
  const [tableData, setTableData] = useState<ITopSchoolLicenseItem[]>([]);
  const [learningStageData, setLearningStageData] = useState<
    TLearningStageGraphItem[]
  >([]);
  const [topSchoolsData, setTopSchoolsData] = useState<TTopSchoolsGraphItem[]>(
    []
  );
  const [subscriptionDistribution, setSubscriptionDistribution] = useState<
    { label: string; value: number; color: string }[]
  >([]);
  const [userEngagementPeriod, setUserEngagementPeriod] = useState('yearly');
  const [userEngagementData, setUserEngagementData] = useState<
    TUserEngagementGraphItem[]
  >([]);

  const totalUserEngagement = useMemo(
    () =>
      userEngagementData.reduce(
        (sum, item) =>
          sum +
          (item?.children ?? 0) +
          (item?.teachers ?? 0) +
          (item?.schools ?? 0),
        0
      ),
    [userEngagementData]
  );
  const { loadingTable, setLoadingTable, loadingSummary, setLoadingSummary } =
    useCommonStore();

  // ── Fetch Learning Stage Graph ─────────────────────────────────────────────
  const fetchLearningStageGraph = async () => {
    const response = await getLearningStageGraph();
    if (response?.status) {
      setLearningStageData(response?.data);
    }
  };

  // ── Fetch Top Schools Graph ────────────────────────────────────────────────
  const fetchTopSchoolsGraph = async () => {
    const response = await getTopSchoolsGraph();
    if (response?.status) {
      setTopSchoolsData(response?.data);
    }
  };

  // ── Fetch Overview Stats ───────────────────────────────────────────────────
  const fetchSummary = async () => {
    setLoadingSummary(true);
    const response = await getOverviewStats();
    if (response?.status && response?.data) {
      const data = response?.data as IReportOverviewStatsData;
      const mapped = OVERVIEW_SUMMARY_CONFIG?.map((item) => {
        const apiItem = data[item?.key as keyof IReportOverviewStatsData];
        return {
          ...item,
          value: apiItem ? String(apiItem?.total) : '0',
          trend: apiItem ? `${apiItem?.percentage}%` : '0%',
          trendDirection: apiItem ? apiItem?.trend : 'STABLE',
          isPositive: apiItem ? apiItem?.isPositive : true,
        };
      });
      setStatsData(mapped);
    }
    setLoadingSummary(false);
  };

  // ── Fetch Top Schools - License Analytics ──────────────────────────────────
  const fetchTopSchoolsLicenseAnalytics = async () => {
    setLoadingTable(true);
    try {
      const response = await getTopSchoolsLicenseAnalytics();
      if (response?.status) {
        setTableData((response?.data as ITopSchoolLicenseItem[]) || []);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error(error);
      setTableData([]);
    } finally {
      setLoadingTable(false);
    }
  };

  // ── Fetch Subscription Distribution ────────────────────────────────────────
  const fetchSubscriptionDistribution = async () => {
    const response = await getSubscriptionDistribution();
    if (response?.status) {
      const data = (response?.data as TSubscriptionDistributionItem[]) ?? [];
      setSubscriptionDistribution(
        data?.map((item, index) => ({
          label: item?.label,
          value: item?.percentage,
          color:
            SUBSCRIPTION_DISTRIBUTION_PALETTE[
              index % SUBSCRIPTION_DISTRIBUTION_PALETTE.length
            ],
        }))
      );
    }
  };

  const fetchUserEngagement = async () => {
    const filter = USER_ENGAGEMENT_PERIOD_FILTER_MAP[userEngagementPeriod] ?? 1;
    const response = await getUserEngagementGraph(filter);
    if (response?.status && Array.isArray(response?.data)) {
      setUserEngagementData(response.data as TUserEngagementGraphItem[]);
    } else {
      setUserEngagementData([]);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchLearningStageGraph();
    fetchTopSchoolsGraph();
    fetchTopSchoolsLicenseAnalytics();
    fetchSubscriptionDistribution();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchUserEngagement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEngagementPeriod]);

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'report-analytics.overview.table.school-name',
      key: 'name',
      width: '25%',
      render: (row: ITopSchoolLicenseItem) => (
        <div className="flex items-center gap-2">
          <AvatarGroupList
            list={[
              { id: row.id, name: row?.name, image: row?.school_logo ?? '' },
            ]}
            size={40}
            avatarClassName="w-8 h-8 text-[10px]"
            isSquare
          />
          <span className="text-sm font-medium text-neutral-primary">
            {row?.name}
          </span>
        </div>
      ),
    },
    {
      title: 'report-analytics.overview.table.active-students',
      key: 'active_students',
      width: '20%',
      render: (row: ITopSchoolLicenseItem) => (
        <span className="text-sm font-medium text-neutral-primary">
          {row.active_students.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'report-analytics.overview.table.status',
      key: 'subscription_status',
      width: '15%',
      render: (row: ITopSchoolLicenseItem) => {
        if (!row.subscription_status) {
          return <span className="text-sm text-neutral-secondary">-</span>;
        }
        const config = SubscriptionStatusConfig[row.subscription_status];
        return (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap',
              config?.className
            )}
          >
            {formatText(config?.label)}
          </span>
        );
      },
    },
    {
      title: 'report-analytics.overview.table.expiry-date',
      key: 'expiry_date',
      width: '20%',
      render: (row: ITopSchoolLicenseItem) => (
        <span className="text-sm text-neutral-secondary whitespace-nowrap">
          {row.expiry_date ? formatUnixToLongDate(row.expiry_date) : '-'}
        </span>
      ),
    },
    {
      title: 'report-analytics.overview.table.engagement',
      key: 'engagement',
      width: '20%',
      render: (row: ITopSchoolLicenseItem) => (
        <span
          className={clsx(
            'text-sm font-semibold',
            row?.engagement >= 80
              ? 'text-success-secondary'
              : row?.engagement >= 60
                ? 'text-warning-secondary'
                : 'text-error-secondary'
          )}
        >
          {`${row?.engagement ?? 0}%`}
        </span>
      ),
    },
  ];

  // const showListUI = loadingTable || tableData?.length > 0;

  return (
    <div className="flex flex-col gap-6">
      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
        {statsData?.map((stat, index) => (
          <SummaryCard
            key={index}
            variant="dashboard"
            label={formatText(stat.label)}
            value={stat?.value}
            icon={stat?.icon}
            trendDirection={stat?.trendDirection}
            isPositive={stat?.isPositive}
            trendLabel={
              stat?.trendLabel ? formatText(stat.trendLabel) : undefined
            }
            trend={stat?.trend}
            isLoading={loadingSummary}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-10">
        <div className="lg:col-span-6 rounded-xl border border-neutral-light-200 bg-card p-4 shadow-2xs">
          <div className="flex flex-wrap items-center justify-between mb-1 gap-2">
            <div className="flex flex-col">
              <h2 className="text-lg font-semibold text-neutral-primary">
                {formatText('report-analytics.overview.user-engagement.title')}
              </h2>
              <span className="text-2xl font-semibold text-neutral-primary">
                {totalUserEngagement?.toLocaleString()}
              </span>
            </div>
            <Select
              options={USER_ENGAGEMENT_PERIOD_OPTIONS}
              selected={userEngagementPeriod}
              onChange={(val) => setUserEngagementPeriod(val as string)}
              className="w-36! py-2!"
              isTextFormat
            />
          </div>
          <UserEngagementOverTimeChart data={userEngagementData} />
        </div>
        <div className="lg:col-span-4 rounded-xl border border-neutral-light-200 bg-card p-4 shadow-2xs flex flex-col gap-6">
          <h2 className="mb-4 text-lg font-semibold text-neutral-primary">
            {formatText(
              'report-analytics.overview.subscription-distribution.title'
            )}
          </h2>
          <SubscriptionDistributionChart data={subscriptionDistribution} />
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-neutral-light-200 bg-card p-4 shadow-2xs">
          <h2 className="text-lg font-semibold text-neutral-primary">
            {formatText(
              'report-analytics.overview.learning-level-distribution.title'
            )}
          </h2>
          <LearningLevelDistributionChart data={learningStageData} />
        </div>

        <div className="rounded-xl border border-neutral-light-200 bg-card p-4 shadow-2xs">
          <h2 className="text-lg font-semibold text-neutral-primary">
            {formatText(
              'report-analytics.overview.school-active-students.title'
            )}
          </h2>
          <SchoolActiveStudentsChart data={topSchoolsData} />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold">
          {formatText('report-analytics.overview.license-analytics.title')}
        </h3>

        <CustomTable
          isLoading={loadingTable}
          dataSource={tableData}
          columns={columns}
          rootClassName="!h-full"
          isEmpty={!loadingTable && tableData?.length === 0}
          emptyImgSrc={'/assets/svg/report-analytics-empty.svg'}
          emptyTitle={formatText('report-analytics.not-found-title')}
          emptyDescription={formatText('report-analytics.not-found-desc')}
          hidePagination={true}
        />
      </div>
    </div>
  );
};

export default OverView;
